import { useEffect, useState } from 'react';
import { Col, Drawer, Form, Input, message, Row, Select, Switch, Tabs, Space, Button, Typography, Divider, Tag } from 'antd';
import { drawerWidth, formItemLayout } from '../../../utils/constants';
import { CheckOutlined, CloseOutlined, LoadingOutlined, SaveOutlined } from '@ant-design/icons';
import UsersServices from '../../../services/usersServices';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Loader from '../../../components/common/Loader';
import { useLoader } from '../../../context/loaderContext';
import LocalfilesServices from '../../../services/localFilesServices';
import { UpdateUserPayload, User, UserLocalFile } from './users.types';
import { getCountryLabel } from '../../../utils/functions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
const { Text } = Typography;

const UpdateUser = ({ open, onClose, recordData }: { open: boolean, onClose: () => void, recordData: User }) => {
    const { t } = useTranslation();
    const countries = useSelector((state: any) => state.utils.countries);
    const { roles } = useSelector((state: any) => state.roles);
    const [userLocalFiles, setUserLocalFiles] = useState<any[]>([]);
    const { setLoading } = useLoader();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();

    useEffect(() => {
        if (recordData) {
            getLocalFiles(recordData.id);

            form.setFieldsValue({
                countries: recordData?.countries?.map((x: any) => x.countryID),
            });
        }
    }, []);

    const updateUserMutation = useMutation({
        mutationFn: UsersServices.updateUser,
        onSuccess: (respData) => {
            if (respData.success) {
                message.success(t('messages.success'));

                queryClient.invalidateQueries({
                    queryKey: ["users"]
                });

                onClose();
            }
            else {
                message.error(t('messages.errorAccured'));
            }
        },

        onError: () => {
            message.error(t('messages.errorAccured'));
        }
    });

    const saveData = (values: UpdateUserPayload) => {        
        updateUserMutation.mutate(values);
        // console.log(values);
    };

    // const saveData = async (values: UpdateUserPayload) => {
    //     setLoadingButton(true);
    //     try {
    //         const respData = await UsersServices.updateUser(values);
    //         if (respData.success) {
    //             message.success(t('messages.success'));
    //             getUsers();
    //             onClose();
    //         }
    //         else {
    //             message.error(t('messages.errorAccured'));
    //         }
    //     } catch (error) {
    //         message.error(t('messages.errorAccured'));
    //     }
    //     finally {
    //         setLoadingButton(false);
    //     }
    // }

    const getLocalFiles = async (userId: string) => {
        setLoading(true);
        try {
            const respData = await LocalfilesServices.getLocalFilesList();
            if (respData.success) {
                const res = respData.responseObject.value.localFiles;
                if (res) {
                    getUserLocalFiles(userId, res);
                }
            }
            else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        } finally {
            setLoading(false);
        }
    };

    const getUserLocalFiles = async (userId: string, localFiles: any[]) => {
        setLoading(true);
        try {
            const respData = await UsersServices.getUserLocalFiles(userId);
            if (respData.success) {
                const res = respData.responseObject.value.userLocalFiles;
                generateUserLocalFilesList(res, localFiles);
            }
            else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        } finally {
            setLoading(false);
        }
    };

    const generateUserLocalFilesList = (userLocalFiles: any[], localFiles: any[]) => {
        if (localFiles) {
            const result = localFiles.map(file => ({
                ...file,
                enabled: userLocalFiles.some(
                    u => u.localFileID === file.id
                ),
            }));
            setUserLocalFiles(result);
        }
    }

    const LocalFiles = ({ recordData }: { recordData: User }) => {
        const [loadingIds, setLoadingIds] = useState<string[]>([]);

        const onLocalFileChange = async (checked: boolean, localFile: any, userData: User) => {

            const payload: UserLocalFile = {
                localFileID: localFile.id,
                userID: userData.id,
                enable: checked
            };

            setLoadingIds((prev: string[]) => [...prev, localFile.id]);
            try {
                const respData = await UsersServices.updateUserLocalFile(payload);
                if (respData.success) {
                    message.success(t('messages.success'));
                }
                else {
                    message.error(t('messages.errorAccured'));
                }
            } catch (error) {
                message.error(t('messages.errorAccured'));
            }
            finally {
                setLoadingIds((prev) => prev.filter((id) => id !== localFile.id));
            }

        }


        return (
            <Row gutter={16}>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                    <Loader />
                </Col>

                {userLocalFiles.map((localFile) => {
                    const isLoading = loadingIds.includes(localFile.id);

                    return (
                        <Col key={localFile.id} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Space style={{ display: "flex", justifyContent: "space-between" }}>
                                <Text>{localFile.name}</Text>
                                {isLoading && <LoadingOutlined spin style={{ fontSize: 16 }} />}
                                <Switch onChange={(checked) => onLocalFileChange(checked, localFile, recordData)} defaultValue={localFile.enabled} checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />

                            </Space>
                            <Divider style={{ margin: "14px 0" }} />
                        </Col>
                    )
                })}
            </Row>
        )
    }

    const items = [
        {
            key: '1',
            label: (t('users.userDetails')),
            children: (
                <Form
                    id="updateUser"
                    form={form}
                    layout="vertical"
                    {...formItemLayout}
                    onFinish={(values) => saveData(values)}
                >
                    <Row gutter={16}>

                        <Form.Item name="id" initialValue={recordData.id} hidden={true}><Input type="hidden" /></Form.Item>

                        <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 12 }} lg={{ span: 12 }}>
                            <Form.Item
                                label={t('users.name')}
                                name="name"
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                                initialValue={recordData.name}
                            >
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 12 }} lg={{ span: 12 }}>
                            <Form.Item
                                label={t('users.lastname')}
                                name="surname"
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                                initialValue={recordData.surname}
                            >
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('users.username')}
                                name="username"
                                initialValue={recordData.username}
                            >
                                <Input disabled />
                            </Form.Item>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('users.email')}
                                name="email"
                                initialValue={recordData.email}
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Input disabled />
                            </Form.Item>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('users.role')}
                                name="roleID"
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                                initialValue={recordData.roleID}
                            >
                                <Select
                                    showSearch
                                    placeholder={t('general.select')}
                                    optionFilterProp="label"
                                    options={roles}
                                />
                            </Form.Item>
                        </Col>


                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t("users.country")}
                                name="countries"
                                rules={[
                                    {
                                        required: true,
                                        message: t("messages.requiredField"),
                                    },
                                ]}
                            >
                                <Select
                                    mode="multiple"                                   
                                    placeholder={t("general.select")}
                                    onInputKeyDown={(event) => {
                                        if (event.key === 'Backspace') {         
                                          event.stopPropagation();
                                        }
                                      }}
                                    options={countries.map((item: any) => ({
                                        value: item.id,
                                        label: getCountryLabel(item),
                                    }))}
                                    onChange={(values) => {
                                        if (values.length === 0) {
                                            message.warning(t("users.atLeastOneCountry"));
                                            form.setFieldsValue({
                                                countries: form.getFieldValue("countries"),
                                            });
                                            return;
                                        }
                                        form.setFieldsValue({
                                            countries: values,
                                        });
                                    }}

                                    tagRender={(props) => {
                                        const selectedCountries = form.getFieldValue("countries") || [];
                                        return (
                                            <Tag
                                                closable={selectedCountries.length > 1}
                                                onClose={props.onClose}
                                                style={{ marginRight: '5px', display: 'inline-flex', fontSize: '14px' }}
                                            >
                                                {props.label}
                                            </Tag>
                                        );
                                    }}
                                />
                            </Form.Item>
                            {/* <Form.Item
                                label={t('users.country')}
                                name="countries"
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                                initialValue={recordData.countryID}
                            >
                                <Select
                                    showSearch
                                    placeholder={t('general.select')}
                                    optionFilterProp="label"
                                    mode='multiple'
                                    // disabled
                                    options={countries.map((item: any) => ({ value: item.id, label: getCountryLabel(item) }))}
                                />
                            </Form.Item> */}
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('users.active')}
                                name="enabled"
                                valuePropName="checked"
                                initialValue={recordData.enabled}
                            >
                                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
                            </Form.Item>
                        </Col>
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Button
                                loading={updateUserMutation.isPending}
                                block
                                icon={<SaveOutlined />}
                                htmlType="submit"
                                key="submit"
                            >
                                {t('general.save')}</Button>
                        </Col>
                    </Row>
                </Form>
            )
        },
        {
            key: '2',
            label: (t('localFiles.localFiles')),
            children: <LocalFiles recordData={recordData} />
        }
    ];

    return (
        <Drawer
            title={t('users.editUser')}
            width={drawerWidth}
            onClose={onClose}
            destroyOnClose={true}
            open={open}
            extra={
                <Space className="drawerButtons">
                    <Button key="close" onClick={onClose}>{t('general.close')}</Button>
                </Space>
            }
        >

            <Tabs defaultActiveKey="1" items={items} />
        </Drawer>
    );
}
export default UpdateUser;